import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Screenshot from "./Screenshot";
import "../bomScheduler/FileUpload.css";
import "./Help.css";

const SECTIONS = [
  { id: "quick-start", title: "Quick start" },
  { id: "desktop", title: "Coming from the desktop tool" },
  { id: "create", title: "Create a test set" },
  { id: "compare-with", title: "What to compare with" },
  { id: "results", title: "Follow the results" },
  { id: "viewers", title: "View and compare BOMs" },
  { id: "agile", title: "Agile and Oracle" },
  { id: "files", title: "The files you get" },
  { id: "tips", title: "Tips" },
  { id: "faq", title: "Questions" },
];

const EXECUTION_OPTIONS = [
  ["Compare Development Vs Production", "On-Premise Dev", "On-Premise Prd"],
  ["Compare Development Vs UAT", "On-Premise Dev", "On-Premise UAT"],
  ["Data From DB", "On-Premise Dev", "Production Database"],
  ["Compare Previous Development (Folder Name)", "On-Premise Dev", "Previous run"],
  ["Compare Previous Production (Folder Name)", "On-Premise Prd", "Previous run"],
  ["Generate only Dev BOM", "On-Premise Dev", "Only get the BOM"],
];

function useActiveSection() {
  const [active, setActive] = useState(SECTIONS[0].id);
  useEffect(() => {
    if (!("IntersectionObserver" in window)) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "0px 0px -70% 0px" }
    );
    SECTIONS.forEach((s) => {
      const element = document.getElementById(s.id);
      if (element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, []);
  return active;
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="help-section" aria-labelledby={`${id}-title`}>
      <h4 id={`${id}-title`}>{title}</h4>
      {children}
    </section>
  );
}

export default function HelpPage() {
  const location = useLocation();
  const active = useActiveSection();

  useEffect(() => {
    document.title = "How it works · DS Simulator Data Forge";
    return () => {
      document.title = "DS Simulator Data Forge";
    };
  }, []);

  useEffect(() => {
    const id = location.hash.replace("#", "");
    if (!id) return;
    const timer = setTimeout(() => document.getElementById(id)?.scrollIntoView(), 50);
    return () => clearTimeout(timer);
  }, [location.hash]);

  return (
    <div className="help-page card border-0 shadow-sm text-start">
      <div className="card-body p-4">
        <Link to="/" className="small">&larr; Back to BOM Compare</Link>
        <h3 className="upload-panel__title mt-2 mb-1">How BOM Compare works</h3>
        <p className="upload-panel__subtitle mb-4">
          BOM Compare runs your codestrings in the Infor configurator, gives you the BOM as Excel and compares it with
          another BOM, so you can see what a rule change does before it goes to production. It replaces the desktop BOM
          Compare and Agile/Oracle BOM Compare tools.
        </p>

        <div className="help-layout">
          <nav className="help-nav" aria-label="Guide sections">
            <ul className="help-nav__list">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className={`help-nav__link${active === s.id ? " is-active" : ""}`} aria-current={active === s.id ? "true" : undefined}>
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <Section id="quick-start" title="Quick start">
              <ol className="help-steps">
                <li>
                  Add your inputs: <b>Import from Excel</b> with a filled template (<b>Download template</b> gets an empty
                  one), or fill the rows with the <b>ESOI</b>, the <b>Format Number</b> and the <b>CodeString</b>.
                </li>
                <li>Choose <b>BOM from</b> (where to simulate) and <b>Compare with</b> (what to compare it with).</li>
                <li>Click <b>Create test set</b>. It runs in the background; allow notifications to get a notice when it finishes.</li>
                <li>Open the test set in <b>Test sets</b>. The <b>Result</b> column tells you what each comparison found.</li>
                <li>Click <b>View BOM</b> or <b>Compare</b> to see it in the browser, or download the Excel files.</li>
              </ol>
              <p>
                Prefer to see it on the page? <Link to="/?tour=1">Take the 1-minute tour</Link>.
              </p>
              <Screenshot
                src="/help/new-test-set.png"
                alt="The New test set form with a row filled in"
                marks={[
                  { n: 1, x: 1.9, y: 31.6, text: "Import from Excel, or Download template to get an empty one." },
                  { n: 2, x: 12, y: 52, text: "BOM from: where the BOM comes from. Click ? next to a field for a short explanation." },
                  { n: 3, x: 37.5, y: 52, text: "Compare with: what it is compared with." },
                  { n: 4, x: 84.6, y: 63.4, text: "Break down: see each code item of the codestring and change one." },
                  { n: 5, x: 84.5, y: 95.6, text: "Create test set." },
                ]}
              />
            </Section>

            <Section id="desktop" title="Coming from the desktop tool">
              <p>
                Everything the desktop tools did is here, with fewer steps. You don't install anything, there is no version
                to update, and your runs are saved, so you can open them again or compare against them later.
              </p>
              <div className="table-responsive">
                <table className="table table-sm help-table">
                  <thead>
                    <tr>
                      <th scope="col">In the desktop tool</th>
                      <th scope="col">Here</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Drag the Excel file onto the .exe</td>
                      <td><b>Import from Excel</b> (choose or drop the file), or fill the rows on the page</td>
                    </tr>
                    <tr>
                      <td>"Regression Testing Orders" template</td>
                      <td>
                        <b>Download template</b>. Same columns for <b>Reference Source SOI Number</b>, <b>Format Number</b>,{" "}
                        <b>Code</b>, <b>Section To Process</b> and <b>Enable Post Config Queue</b>. Test, Summary of Change and
                        String Length can stay, they are ignored.
                      </td>
                    </tr>
                    <tr>
                      <td>Execution Option column</td>
                      <td>Two columns, <b>Environment</b> and <b>Compare With</b> (on the page: <b>BOM from</b> and <b>Compare with</b>). See the table below.</td>
                    </tr>
                    <tr>
                      <td>Folder Name of an earlier run</td>
                      <td>
                        <b>Compare with: Previous run</b> and pick the run, or its Id (#12345) in <b>Previous Run Id</b>. Empty
                        uses your latest run of the same ESOI.
                      </td>
                    </tr>
                    <tr>
                      <td>Console window with the progress</td>
                      <td>The <b>Test sets</b> list: progress and status of each input, refreshed on its own</td>
                    </tr>
                    <tr>
                      <td>Outputs\yyyyMMdd_HHmm folder</td>
                      <td>
                        Open the test set: each input has its files. <b>Download all files</b> gets every BOM and comparison in
                        one .zip
                      </td>
                    </tr>
                    <tr>
                      <td>RawOutputs\ files</td>
                      <td><b>Raw BOM</b> files, one per environment. <b>View BOM</b> opens one in the browser</td>
                    </tr>
                    <tr>
                      <td>_Comp.xlsx files and their Summary tab</td>
                      <td>
                        <b>Comparison</b> files, same layout. The <b>Result</b> column shows the Summary counts without
                        opening the Excel, and <b>Compare</b> shows the differences in the browser
                      </td>
                    </tr>
                    <tr>
                      <td>"One or both raw files are empty" in the Actions column</td>
                      <td>The input shows <b>Failed</b> with the error from the configurator under it</td>
                    </tr>
                    <tr>
                      <td>Agile/Oracle tool: ESOI Number, Fabric and the ECO cell</td>
                      <td>
                        Same template as Infor: the ESOI or work order in <b>Reference Source SOI Number</b>, <b>Plant</b> and{" "}
                        <b>Include ECO Details</b>. See <a href="#agile">Agile and Oracle</a>
                      </td>
                    </tr>
                    <tr>
                      <td>Agile service account and VPN check</td>
                      <td>You sign in to Agile with your own user, once, from the page</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h6>Execution Option, now BOM from and Compare with</h6>
              <div className="table-responsive">
                <table className="table table-sm help-table">
                  <thead>
                    <tr>
                      <th scope="col">Execution Option</th>
                      <th scope="col">BOM from</th>
                      <th scope="col">Compare with</th>
                    </tr>
                  </thead>
                  <tbody>
                    {EXECUTION_OPTIONS.map(([option, from, compare]) => (
                      <tr key={option}>
                        <td>{option}</td>
                        <td>{from}</td>
                        <td>{compare}</td>
                      </tr>
                    ))}
                    <tr>
                      <td>Compare Folders</td>
                      <td colSpan={2}>
                        Not needed: use <b>Rerun</b> on the old test set, or create a new one with <b>Compare with: Previous run</b>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="help-callout">
                Your old input file can't be imported as it is, because Execution Option and Folder Name are no longer
                columns. Copy your rows into the new template and fill <b>Environment</b> and <b>Compare With</b>; the rest
                of the columns are the same.
              </div>

              <h6>What's new compared with the desktop tool</h6>
              <ul>
                <li>Every run is saved with an Id. Open it again, compare against it later or rerun it.</li>
                <li>Compare Infor with the real order in Agile, Oracle BOM or Oracle BOM Viewer.</li>
                <li><b>Break down</b> a codestring into its code items and change one without counting positions.</li>
                <li>See the BOM and the differences in the browser, with search and filters, without opening Excel.</li>
                <li>A notification when your test set finishes.</li>
              </ul>
            </Section>

            <Section id="create" title="Create a test set">
              <p>
                A test set is one group of inputs that run together, like one run of the desktop tool. Give it a <b>Name</b>{" "}
                if you like; without one it uses the Excel file name, or Test set #number. You can rename it later with the
                pencil.
              </p>
              <h6>From Excel or on the page</h6>
              <ul>
                <li>
                  <b>Import from Excel</b>: choose or drop a filled template. The template has an Instructions sheet and a
                  list in each column with the allowed values. If a row has a problem, it is skipped and the reason is shown.
                </li>
                <li>
                  On the page: fill a row with the <b>ESOI</b>, the <b>Format Number</b> and the <b>CodeString</b>.{" "}
                  <b>Duplicate</b> copies a row, for example to run the same codestring in another environment.{" "}
                  <b>+ Add row</b> adds an empty one.
                </li>
              </ul>
              <h6>BOM from</h6>
              <p>
                Where the BOM comes from. <b>On-Premise Dev, Prd or UAT</b> simulate the codestring in Infor. <b>Agile</b>, <b>Oracle BOM</b> and <b>Oracle BOM Viewer</b> read a
                real order; no codestring is needed.
              </p>
              <h6>Break down</h6>
              <p>
                <b>Break down</b>, above the CodeString, lists every code item of the codestring from RDM with its options.
                Pick another option, or type a value for items like fan size, and <b>Apply</b> writes the new codestring in
                the row.
              </p>
              <Screenshot
                src="/help/break-down.png"
                alt="The Break down dialog listing the code items of a codestring with an Option column"
                marks={[
                  { n: 1, x: 18.5, y: 13.9, text: "The codestring, with the changed positions highlighted." },
                  { n: 2, x: 48, y: 49.9, text: "Pick another option, or type the value. Undo puts the old one back." },
                  { n: 3, x: 95.5, y: 90.8, text: "Apply writes the new codestring in the row." },
                ]}
              />
              <h6>Options of each row</h6>
              <ul>
                <li><b>Post Config Queue</b>: saves the simulator trace file. On-premise only.</li>
                <li>
                  <b>Only one section</b>: the simulator runs only the section in <b>Section To Process</b> and returns only
                  its data. The switch adds <code>_sectonly</code> to the ESOI for you; in Excel, add it yourself. On-premise
                  only.
                </li>
              </ul>
            </Section>

            <Section id="compare-with" title="What to compare with">
              <p>The list only shows what can be compared with the BOM from you chose.</p>
              <ul>
                <li><b>Another environment</b>: the codestring is simulated there too and both BOMs are compared.</li>
                <li><b>Production Database</b>: the latest BOM saved in production for that ESOI. Nothing is simulated for it.</li>
                <li>
                  <b>Previous run</b>: one of your earlier runs, picked from a list (search by ESOI or #Id). Nothing is
                  simulated for it.
                </li>
                <li>
                  <b>An order in Agile, Oracle BOM or Oracle BOM Viewer</b>: compares the simulation with the BOM of a real
                  order. Enter its ESOI or work order in <b>Agile/Oracle ESOI or Work Order</b>; the ESOI of the row only
                  names the simulation. Items are matched by bubble, and generated part numbers are compared without the SOI
                  prefix. Revisions and raw material are not compared, Infor doesn't have them.
                </li>
                <li><b>Only get the BOM</b>: no comparison, you just get the Raw BOM.</li>
              </ul>
            </Section>

            <Section id="results" title="Follow the results">
              <p>
                Your test sets are listed under the form, newest first, with their progress. Click one, or{" "}
                <b>View inputs</b>, to open it.
              </p>
              <Screenshot
                src="/help/test-set-inputs.png"
                alt="The inputs of a test set, with the Result column and the actions of each input"
                marks={[
                  { n: 1, x: 12.3, y: 18.8, text: "Download all files: every BOM and comparison of the test set in one .zip." },
                  { n: 2, x: 3.8, y: 27.3, text: "Test / Run: the run Id (#52011), to find it when you compare with a previous run." },
                  { n: 3, x: 57, y: 27, text: "Result: what the comparison found. Red is NOT FOUND or ERROR. Click a count to open those rows." },
                  { n: 4, x: 80.5, y: 27, text: "View BOM, Compare, Excel and Rerun." },
                ]}
              />
              <ul>
                <li>
                  <b>Status</b>: queued, running, Done or Failed. When an input fails, the error from the configurator shows
                  under its status.
                </li>
                <li>
                  <b>Queue</b>: up to three Infor inputs run at a time. When several people are waiting, each free slot
                  goes to the person with the fewest inputs running, so a big test set does not hold everyone else back.
                </li>
                <li>
                  <b>Result</b>: what the comparison found (missing items, changes, quantities), read from its Summary sheet.
                  Hover it or expand the input to see every count. For Infor runs, click a count to open those rows in the
                  comparison.
                </li>
                <li>
                  <b>View BOM</b> and <b>Compare</b> open the BOM or the comparison in a new tab. <b>Excel</b> downloads the
                  comparison files; for Agile and Oracle, <b>Comparison</b> downloads the result.
                </li>
                <li>Expand an input (the arrow on the left) to see its codestring and all its files.</li>
                <li><b>Rerun</b> runs the inputs again as a new test set; the old results are kept.</li>
              </ul>
            </Section>

            <Section id="viewers" title="View and compare BOMs">
              <p>
                <b>View BOM</b> opens one BOM as a tree, like the Excel but easier to read: search, filter by type, choose
                columns and click a row to see all its values. It works for every source: Infor, Production Database,
                Agile and Oracle.
              </p>
              <p>
                <b>Compare</b> (Infor runs) shows the same tree with only the differences. Each changed cell shows both
                values. Switch between the two BOMs and the comparison at the top; the number on each tab is how many rows
                differ.
              </p>
              <p>
                <b>Inputs</b>, on the left, lists every input of the same test set. Click one to open it in the same tab
                and with the same filters. Hide the list with the button next to its title to give the table the whole
                width. <b>Break down</b>, next to the codestring, lists its code items and options from RDM, read only.
              </p>
              <Screenshot
                src="/help/comparison-viewer.png"
                alt="The comparison viewer with the inputs of the test set on the left and changed rows with both values"
                marks={[
                  { n: 1, x: 18, y: 14.4, text: "Break down: the code items of the codestring and their options, read only." },
                  { n: 2, x: 12.3, y: 23.6, text: "Hide the list of inputs; the bar that stays on the left shows it again." },
                  { n: 3, x: 8.2, y: 42.4, text: "Every input of the test set. Search by ESOI, test or codestring." },
                  { n: 4, x: 70.2, y: 8.3, text: "Switch between the BOM of each environment and the comparison." },
                  { n: 5, x: 86.3, y: 3.8, text: "Copy link to share this view." },
                  { n: 6, x: 28.4, y: 19.4, text: "Tabs of the file; the number is how many rows differ." },
                  { n: 7, x: 55.8, y: 28.3, text: "Search, switch between tree and list, and choose columns." },
                  { n: 8, x: 56.2, y: 34.3, text: "Show only changed, added or deleted rows, or one type of part." },
                  { n: 9, x: 60, y: 58.1, text: "A changed cell shows both values, Prd crossed out and then Dev, like the Quantity 2 → 4 here." },
                ]}
              />
              <p>
                <b>Copy link</b> copies the link to what you see, with the same tab and filters. Send it to anyone who can
                sign in to BOM Compare and they open the same view.
              </p>
            </Section>

            <Section id="agile" title="Agile and Oracle">
              <p>
                In <b>BOM from</b> choose Agile, Oracle BOM or Oracle BOM Viewer, and in <b>Compare with</b> the other one:
              </p>
              <ul>
                <li><b>Agile</b> with <b>Oracle BOM Viewer (+ Oracle BOM)</b>: the three in one run, like the desktop Agile/Oracle tool.</li>
                <li><b>Agile</b> with <b>Oracle BOM</b>.</li>
                <li><b>Oracle BOM</b> with <b>Oracle BOM Viewer</b>: what changed in Oracle since the work order snapshot.</li>
                <li><b>Only get the BOM</b>: just the BOM of the order.</li>
              </ul>
              <ul>
                <li>
                  <b>Sign in to AGILE</b> with your own Agile user the first time. Your password is only used to sign in and
                  is not stored. Agile ends the session after a while without use; you are asked to sign in again when
                  needed. <b>Change credentials</b> switches users.
                </li>
                <li><b>ESOI or Work Order</b>: enter one, the other one is found in Oracle.</li>
                <li><b>Plant</b>: the Oracle plant of the BOM (SLP, SL2, SL3, FBO or STN).</li>
                <li>
                  <b>Agile version</b>: <b>Load versions</b> lists the versions of the order so you can pick one;{" "}
                  <b>Default</b> reads the one Agile opens. In Excel, write it in <b>Agile Revision</b> (e.g. 0B, or its
                  change number).
                </li>
                <li>
                  <b>Oracle BOM</b> is the current BOM, always the latest version. <b>Oracle BOM Viewer</b> is the snapshot
                  saved for a work order, so it can be older.
                </li>
                <li>
                  <b>ECO Details</b>: for items with a different revision, adds the Agile ECO that released it. It takes
                  longer, so choose Only DFT/FAB Comp or All items only when you need it.
                </li>
                <li>You get one Comparison file with the same tabs as the desktop tool: Summary, Comp, and the AB, OB and OBV BOMs.</li>
              </ul>
            </Section>

            <Section id="files" title="The files you get">
              <ul>
                <li><b>Raw BOM</b>: the BOM of one environment or order.</li>
                <li>
                  <b>Comparison</b>: one file per side with a Summary tab. Changed rows are highlighted, and rows that were
                  added or deleted are marked.
                </li>
                <li><b>Trace</b>: the simulator trace file, for on-premise runs with Post Config Queue on.</li>
              </ul>
            </Section>

            <Section id="tips" title="Tips">
              <div className="help-callout help-callout--warning">
                <b>NOT FOUND or ERROR</b> in Part Number means the part could not be resolved. A BOM with one must never be
                released to Agile. It shows in red in the Result column and in the viewers.
              </div>
              <ul>
                <li>The ESOI can't have + &amp; # % ? / or \, the simulator can't open it.</li>
                <li>Each run has an Id (#12345). Use it to find the run when you compare with a previous run.</li>
                <li>Keep the page open if you want the notification when a test set finishes. The run itself doesn't need it.</li>
              </ul>
            </Section>

            <Section id="faq" title="Questions">
              <div className="help-faq">
                <details>
                  <summary>Do I have to keep the page open while it runs?</summary>
                  <p>No. The inputs run on the server. Close the page and open it later, your test set is in the list. Keep it open only to get the notification.</p>
                </details>
                <details>
                  <summary>Where is my output folder?</summary>
                  <p>There is no folder. Open the test set to get each file, or use Download all files for one .zip with all of them.</p>
                </details>
                <details>
                  <summary>Can I use my old desktop input file?</summary>
                  <p>Not as it is. Copy the rows into the new template and replace Execution Option with Environment and Compare With. See <a href="#desktop">Coming from the desktop tool</a>.</p>
                </details>
                <details>
                  <summary>How do I compare two runs I already have?</summary>
                  <p>Create a new test set with Compare with: Previous run and pick the older run. The codestring is simulated again and compared with that run.</p>
                </details>
                <details>
                  <summary>An input failed. What now?</summary>
                  <p>Open the test set: the error from the configurator shows under the status of the input. Check the ESOI, the Format Number and the codestring, then use Rerun.</p>
                </details>
                <details>
                  <summary>Can someone else see my results?</summary>
                  <p>Yes. Open the BOM or the comparison, click Copy link and send it; anyone who can sign in to BOM Compare can open it. Your Test sets list still shows only your own runs.</p>
                </details>
              </div>
            </Section>

            <p className="small text-muted mb-0">
              Questions or ideas? Email <a href="mailto:marco.nunez@daikinapplied.com">marco.nunez@daikinapplied.com</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
